/**
 * RTP normalisation. Canonical unit is a 0–1 fraction (rgs-domain skill). The data
 * and several endpoints mix units, so every RTP passes through here once and the
 * UI can flag bad values instead of guessing silently.
 */
export type RtpIssue = 'missing' | 'percent-like' | 'out-of-range';

export interface NormalizedRtp {
  /** 0–1 fraction, or null when the value cannot be interpreted. */
  fraction: number | null;
  issue?: RtpIssue;
}

const PLAUSIBLE_MIN = 0.5;
const PLAUSIBLE_MAX = 1.0;

export function normalizeRtp(raw: unknown): NormalizedRtp {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n === 0) return { fraction: null, issue: 'missing' };
  if (n >= PLAUSIBLE_MIN && n <= PLAUSIBLE_MAX) return { fraction: n };
  if (n >= PLAUSIBLE_MIN * 100 && n <= PLAUSIBLE_MAX * 100) {
    // Round away float noise (94.74 / 100 = 0.9473999…); DECIMAL(10,5) never needs more precision.
    return { fraction: Math.round((n / 100) * 1e7) / 1e7, issue: 'percent-like' };
  }
  return { fraction: null, issue: 'out-of-range' };
}

/** `96.00%` style label; `—` when unknown. */
export function formatRtp(fraction: number | null, digits = 2): string {
  return fraction === null ? '—' : `${(fraction * 100).toFixed(digits)}%`;
}
